import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, MenuItem, TextField, Button, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  IconButton, Tooltip, CircularProgress, Chip, Autocomplete, Grid, Paper, LinearProgress, Divider, Rating, Collapse,
} from '@mui/material';
import {
  Home, Insights, Search, TrendingUp, TrendingDown, TrendingFlat, Assessment, AutoAwesome,
  Save, CheckCircle, Psychology, ExpandMore, ExpandLess, Analytics,
} from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Pathway accent colors
const PATHWAY_COLORS = {
  'STEM':                  '#1565c0',
  'Social Sciences':       '#6a1b9a',
  'Arts & Sports Science': '#e65100',
};
// CBC band colors, keyed by 2-letter prefix so 8-level codes (EE1, ME2…) resolve too
const GRADE_COLORS = {
  EE: { bg: '#e8f5e9', text: '#2e7d32' },
  ME: { bg: '#e3f2fd', text: '#1565c0' },
  AE: { bg: '#fff3e0', text: '#f57c00' },
  BE: { bg: '#ffebee', text: '#c62828' },
};
const gradeStyle = (g) => GRADE_COLORS[g] || GRADE_COLORS[(g || '').slice(0, 2)] || { bg: '#f5f5f5', text: '#9e9e9e' };

const GradeChip = ({ grade }) => {
  const s = gradeStyle(grade);
  return <Chip label={grade || '—'} size="small" sx={{ bgcolor: s.bg, color: s.text, fontWeight: 700, height: 20 }} />;
};

const Trend = ({ v }) => {
  if (v === null || v === undefined) return <Typography variant="caption" color="text.disabled">—</Typography>;
  if (v > 0) return <Box display="inline-flex" alignItems="center" color="success.main"><TrendingUp fontSize="inherit" sx={{ mr: 0.3 }} />+{v}</Box>;
  if (v < 0) return <Box display="inline-flex" alignItems="center" color="error.main"><TrendingDown fontSize="inherit" sx={{ mr: 0.3 }} />{v}</Box>;
  return <Box display="inline-flex" alignItems="center" color="text.secondary"><TrendingFlat fontSize="inherit" sx={{ mr: 0.3 }} />0</Box>;
};

// Minimal markdown render for the AI guidance: bold headers, bullets, inline **bold**
const inlineBold = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
  /^\*\*[^*]+\*\*$/.test(part) ? <strong key={j}>{part.slice(2, -2)}</strong> : part);
const renderNarrative = (md = '') => md.split('\n').map((line, i) => {
  const t = line.trim();
  if (!t) return <Box key={i} sx={{ height: 6 }} />;
  const h = t.match(/^\*\*(.+?)\*\*:?$/);
  if (h) return <Typography key={i} variant="subtitle2" fontWeight={700} sx={{ mt: 1, color: '#4a148c' }}>{h[1]}</Typography>;
  if (/^[-*]\s+/.test(t)) return <Typography key={i} variant="body2" sx={{ ml: 1.5 }}>• {inlineBold(t.replace(/^[-*]\s+/, ''))}</Typography>;
  return <Typography key={i} variant="body2" sx={{ mt: 0.3 }}>{inlineBold(t)}</Typography>;
});

export default function PathwayInsight() {
  const navigate = useNavigate();
  const [learners, setLearners] = useState([]);
  const [years, setYears]       = useState([]);
  const [sel, setSel]   = useState(null);
  const [year, setYear] = useState('');
  const [term, setTerm] = useState('');   // '' = all terms
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  // AI narrative (Layer 2)
  const [narr, setNarr]           = useState(null);
  const [narrLoading, setNarrLoading] = useState(false);
  const [narrErr, setNarrErr]     = useState('');
  // Teacher review + persistence
  const [saved, setSaved]     = useState(null);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving]   = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [saveOk, setSaveOk]   = useState('');
  // Interest questionnaire
  const [interestItems, setInterestItems]   = useState([]);
  const [interestResp, setInterestResp]     = useState({});   // { itemId: 1..5 }
  const [interestScores, setInterestScores] = useState(null); // { pathways:[{pathway,score}], answeredCount }
  const [intSaving, setIntSaving] = useState(false);
  const [intMsg, setIntMsg]       = useState('');
  const [interestsOpen, setInterestsOpen] = useState(false); // collapsed by default — focus on pathway

  useEffect(() => {
    axios.get(`${API}/api/academics/learners`, { headers: hdrs() }).then(r => setLearners(r.data || [])).catch(() => {});
    axios.get(`${API}/api/academics/years`, { headers: hdrs() })
      .then(r => { setYears(r.data || []); const a = (r.data || []).find(y => y.isActive); if (a) setYear(a.year); })
      .catch(() => {});
  }, []);

  const run = useCallback(async () => {
    if (!sel || !year) { setError('Select a learner and academic year'); return; }
    setLoading(true); setError(''); setData(null);
    setNarr(null); setNarrErr(''); setSaved(null); setRemarks(''); setSaveErr(''); setSaveOk('');
    setInterestItems([]); setInterestResp({}); setInterestScores(null); setIntMsg('');
    try {
      const params = { admNo: sel.admNo, academicYear: year };
      if (term) params.term = term;
      const r = await axios.get(`${API}/api/academics/assessment-scores/pathway-insight`, { headers: hdrs(), params });
      setData(r.data);
      // Load the interest questionnaire + any saved responses (per year, term-independent)
      try {
        const iRes = await axios.get(`${API}/api/academics/assessment-scores/pathway-interest`, { headers: hdrs(), params: { admNo: sel.admNo, academicYear: year } });
        setInterestItems(iRes.data.items || []);
        setInterestResp(Object.fromEntries((iRes.data.responses || []).map(x => [x.id, x.value])));
        setInterestScores(iRes.data.scores || null);
      } catch { /* interest optional */ }
      // Load any previously saved / reviewed guidance for this learner-year-term
      try {
        const g = await axios.get(`${API}/api/academics/assessment-scores/pathway-guidance`, { headers: hdrs(), params });
        if (g.data) {
          setSaved(g.data);
          if (g.data.aiNarrative) setNarr({ narrative: g.data.aiNarrative, model: g.data.aiModel, recommendedPathway: g.data.recommendedPathway });
          setRemarks(g.data.teacherRemarks || '');
        }
      } catch { /* no saved guidance — fine */ }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load pathway insight');
    } finally { setLoading(false); }
  }, [sel, year, term]);

  // Auto-refresh when the learner (or year/term) changes — no need to click Analyse.
  useEffect(() => {
    if (sel && year) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, year, term]);

  const genNarrative = useCallback(async () => {
    if (!sel || !year) return;
    setNarrLoading(true); setNarrErr(''); setNarr(null);
    try {
      const body = { admNo: sel.admNo, academicYear: year };
      if (term) body.term = term;
      const r = await axios.post(`${API}/api/academics/assessment-scores/pathway-narrative`, body, { headers: hdrs() });
      setNarr(r.data);
    } catch (err) {
      setNarrErr(err.response?.data?.error || 'Failed to generate AI guidance');
    } finally { setNarrLoading(false); }
  }, [sel, year, term]);

  const save = useCallback(async (status) => {
    if (!sel || !year || !narr) return;
    setSaving(true); setSaveErr(''); setSaveOk('');
    try {
      const body = {
        admNo: sel.admNo, academicYear: year, term: term || undefined,
        recommendedPathway: narr.recommendedPathway || data?.recommendedPathway || '',
        aiNarrative: narr.narrative, aiModel: narr.model, teacherRemarks: remarks, status,
      };
      const r = await axios.post(`${API}/api/academics/assessment-scores/pathway-guidance`, body, { headers: hdrs() });
      setSaved(r.data);
      setSaveOk(status === 'Reviewed' ? 'Guidance approved and saved.' : 'Draft saved.');
    } catch (err) {
      setSaveErr(err.response?.data?.error || 'Failed to save guidance');
    } finally { setSaving(false); }
  }, [sel, year, term, narr, remarks, data]);

  const saveInterest = useCallback(async () => {
    if (!sel || !year) return;
    setIntSaving(true); setIntMsg('');
    try {
      const responses = Object.entries(interestResp)
        .filter(([, v]) => v >= 1 && v <= 5)
        .map(([id, value]) => ({ id, value }));
      const r = await axios.post(`${API}/api/academics/assessment-scores/pathway-interest`,
        { admNo: sel.admNo, academicYear: year, responses }, { headers: hdrs() });
      setInterestScores(r.data.scores || null);
      setIntMsg('Interests saved. Generate (or regenerate) AI guidance to blend them in.');
    } catch (err) {
      setIntMsg(err.response?.data?.error || 'Failed to save interests');
    } finally { setIntSaving(false); }
  }, [sel, year, interestResp]);

  const maxPathwayPct = data?.pathways?.length ? Math.max(...data.pathways.map(p => p.meanPct), 1) : 1;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 3, mb: 4, px: 2 }}>
      <Card elevation={3} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Insights sx={{ color: '#00695c' }} />
              <Typography variant="h5" sx={{ color: '#00695c', fontWeight: 700 }}>Pathway Insight</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="LearnAlytics — performance over time"><IconButton onClick={() => navigate('/academics/lernalytics', sel ? { state: { learnerAdmNo: sel.admNo } } : undefined)} sx={{ bgcolor: '#e0f2f1' }}><Analytics sx={{ color: '#00838f' }} /></IconButton></Tooltip>
              <Tooltip title="Assessment Reports"><IconButton onClick={() => navigate('/academics/assessment-report')} sx={{ bgcolor: '#e0f2f1' }}><Assessment sx={{ color: '#00695c' }} /></IconButton></Tooltip>
              <Tooltip title="Academics"><IconButton onClick={() => navigate('/academics')}><Home /></IconButton></Tooltip>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            <Autocomplete
              options={learners}
              getOptionLabel={o => `${o.admNo} — ${o.name}`}
              onChange={(_, v) => setSel(v)}
              sx={{ minWidth: 280 }}
              renderInput={p => <TextField {...p} label="Search Learner" size="small" placeholder="Type to filter…" />}
            />
            <TextField select size="small" label="Academic Year" sx={{ minWidth: 130 }} value={year} onChange={e => setYear(e.target.value)}>
              {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}{y.isActive ? ' (Active)' : ''}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Term" sx={{ width: 130 }} value={term} onChange={e => setTerm(e.target.value)}>
              <MenuItem value=""><em>All terms</em></MenuItem>
              {[1, 2, 3].map(t => <MenuItem key={t} value={String(t)}>Term {t}</MenuItem>)}
            </TextField>
            <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
              onClick={run} disabled={loading} sx={{ bgcolor: '#00695c', '&:hover': { bgcolor: '#004d40' } }}>
              {loading ? 'Loading…' : 'Analyse'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {data && (
        <Card elevation={2}>
          <CardContent>
            {/* Learner header + overall */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={2}>
              <Box>
                <Typography variant="h6" fontWeight={800}>{data.learner?.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Adm {data.learner?.admNo} · {data.learner?.stage}{data.learner?.classCode ? ` · ${data.learner.classCode}` : ''} · Year {data.academicYear}
                  {data.termsAnalysed?.length ? ` · Term${data.termsAnalysed.length > 1 ? 's' : ''} ${data.termsAnalysed.join(', ')}` : ''}
                </Typography>
              </Box>
              <Paper variant="outlined" sx={{ px: 2, py: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Overall Mean</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6" fontWeight={800}>{data.overall?.meanPct ?? '—'}%</Typography>
                  <GradeChip grade={data.overall?.grade} />
                </Box>
              </Paper>
            </Box>

            {data.subjectCount === 0 ? (
              <Alert severity="info">No assessment scores found for this learner in the selected period.</Alert>
            ) : (
              <>
                {/* Interest questionnaire — a separate "drawn to" signal, blended by the AI */}
                {interestItems.length > 0 && (
                  <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap"
                      onClick={() => setInterestsOpen(o => !o)}
                      sx={{ cursor: 'pointer', userSelect: 'none' }}>
                      <Psychology sx={{ color: '#00695c', fontSize: 20 }} />
                      <Typography variant="subtitle2" fontWeight={700}>Interests &amp; aptitude</Typography>
                      {interestScores?.pathways?.length > 0 && !interestsOpen && (
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {interestScores.pathways.map(p => (
                            <Chip key={p.pathway} size="small" label={`${p.pathway}: ${p.score}%`}
                              sx={{ height: 20, bgcolor: `${PATHWAY_COLORS[p.pathway] || '#607d8b'}18`, color: PATHWAY_COLORS[p.pathway] || '#607d8b', fontWeight: 600 }} />
                          ))}
                        </Box>
                      )}
                      <Box flex={1} />
                      <Typography variant="caption" color="text.secondary">{interestsOpen ? 'Hide' : 'Optional — expand'}</Typography>
                      {interestsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </Box>
                    <Collapse in={interestsOpen} timeout="auto" unmountOnExit>
                    <Typography variant="caption" color="text.secondary" display="block" mt={1} mb={0.5}>1 = not at all · 5 = very much</Typography>
                    <Grid container>
                      {interestItems.map(it => (
                        <Grid item xs={12} key={it.id}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} py={0.3}>
                            <Typography variant="body2">{it.text}</Typography>
                            <Rating size="small" max={5} value={interestResp[it.id] || 0}
                              onChange={(_, v) => setInterestResp(r => ({ ...r, [it.id]: v || 0 }))} />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mt={1.5}>
                      <Button size="small" variant="outlined" disabled={intSaving}
                        startIcon={intSaving ? <CircularProgress size={14} color="inherit" /> : <Save />}
                        onClick={saveInterest} sx={{ color: '#00695c', borderColor: '#80cbc4', '&:hover': { borderColor: '#00695c' } }}>
                        Save interests
                      </Button>
                      {interestScores?.pathways?.map(p => (
                        <Chip key={p.pathway} size="small" label={`${p.pathway}: ${p.score}%`}
                          sx={{ bgcolor: `${PATHWAY_COLORS[p.pathway] || '#607d8b'}18`, color: PATHWAY_COLORS[p.pathway] || '#607d8b', fontWeight: 600 }} />
                      ))}
                    </Box>
                    {intMsg && <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>{intMsg}</Typography>}
                    </Collapse>
                  </Paper>
                )}

                {/* AI guidance (Layer 2) + teacher review */}
                <Box mb={2}>
                  <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                    <Button size="small" variant="outlined"
                      startIcon={narrLoading ? <CircularProgress size={14} color="inherit" /> : <AutoAwesome />}
                      onClick={genNarrative} disabled={narrLoading}
                      sx={{ color: '#6a1b9a', borderColor: '#ce93d8', '&:hover': { borderColor: '#6a1b9a', bgcolor: '#f8f0fc' } }}>
                      {narrLoading ? 'Generating…' : (narr ? 'Regenerate AI guidance' : 'Generate AI guidance')}
                    </Button>
                    {saved?.status === 'Reviewed'
                      ? <Chip size="small" icon={<CheckCircle />} color="success" variant="outlined" label={`Reviewed${saved.reviewedBy ? ` by ${saved.reviewedBy}` : ''}`} />
                      : saved?.status === 'Draft' && <Chip size="small" label="Saved draft" sx={{ bgcolor: '#eeeeee' }} />}
                  </Box>
                  {narrErr && <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setNarrErr('')}>{narrErr}</Alert>}
                  {narr && (
                    <Paper variant="outlined" sx={{ mt: 1.5, p: 2, bgcolor: '#faf5ff', borderColor: '#e1bee7' }}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                        <AutoAwesome sx={{ color: '#6a1b9a', fontSize: 18 }} />
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#6a1b9a' }}>AI Pathway Guidance</Typography>
                        <Chip size="small" label="Draft — review before sharing" sx={{ ml: { sm: 'auto' }, bgcolor: '#f3e5f5', color: '#6a1b9a' }} />
                      </Box>
                      <Box>{renderNarrative(narr.narrative)}</Box>
                      <Typography variant="caption" color="text.disabled" display="block" mt={1.5}>
                        Generated by {narr.model} · Anonymised — no learner name or admission number was sent to the AI.
                      </Typography>

                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Teacher review</Typography>
                      <TextField fullWidth multiline minRows={2} size="small"
                        placeholder="Add your remarks / context before approving…"
                        value={remarks} onChange={e => setRemarks(e.target.value)} />
                      <Box display="flex" gap={1} mt={1.5} flexWrap="wrap" alignItems="center">
                        <Button size="small" variant="outlined" startIcon={<Save />} disabled={saving} onClick={() => save('Draft')}>Save draft</Button>
                        <Button size="small" variant="contained" disabled={saving}
                          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />}
                          onClick={() => save('Reviewed')} sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>Approve &amp; save</Button>
                        {saved?.reviewedAt && (
                          <Typography variant="caption" color="text.secondary">Reviewed {new Date(saved.reviewedAt).toLocaleDateString()}</Typography>
                        )}
                      </Box>
                      {saveErr && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setSaveErr('')}>{saveErr}</Alert>}
                      {saveOk && <Alert severity="success" sx={{ mt: 1 }} onClose={() => setSaveOk('')}>{saveOk}</Alert>}
                    </Paper>
                  )}
                </Box>

                {/* Recommended pathway + lean bars */}
                {data.recommendedPathway && (
                  <Box mb={1}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      Pathway lean —{' '}
                      <span style={{ color: PATHWAY_COLORS[data.recommendedPathway] || '#000' }}>
                        leaning {data.recommendedPathway}
                      </span>
                    </Typography>
                    {data.pathways.map(p => {
                      const c = PATHWAY_COLORS[p.pathway] || '#607d8b';
                      return (
                        <Box key={p.pathway} mb={1}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.3}>
                            <Typography variant="body2" fontWeight={p.pathway === data.recommendedPathway ? 700 : 500}>
                              {p.pathway} <Typography component="span" variant="caption" color="text.secondary">({p.subjectCount} subj: {p.subjects.join(', ')})</Typography>
                            </Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ color: c }}>{p.meanPct}%</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={Math.min((p.meanPct / maxPathwayPct) * 100, 100)}
                            sx={{ height: 8, borderRadius: 4, bgcolor: `${c}22`, '& .MuiLinearProgress-bar': { bgcolor: c } }} />
                        </Box>
                      );
                    })}
                    {data.unclassifiedSubjects?.length > 0 && (
                      <Typography variant="caption" color="text.disabled">
                        Not mapped to a pathway: {data.unclassifiedSubjects.join(', ')}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Strengths / weaknesses */}
                <Grid container spacing={2} sx={{ my: 0.5 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" fontWeight={700} color="success.main">STRENGTHS</Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                      {data.strengths.map(s => <Chip key={s.code} size="small" label={`${s.name} · ${s.meanPct}% (${s.grade || '—'})`} sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />)}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" fontWeight={700} color="error.main">NEEDS SUPPORT</Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                      {data.weaknesses.map(s => <Chip key={s.code} size="small" label={`${s.name} · ${s.meanPct}% (${s.grade || '—'})`} sx={{ bgcolor: '#ffebee', color: '#c62828' }} />)}
                    </Box>
                  </Grid>
                </Grid>

                {/* Subjects table */}
                <TableContainer sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Subject</strong></TableCell>
                        <TableCell align="center"><strong>Mean %</strong></TableCell>
                        <TableCell align="center"><strong>Grade</strong></TableCell>
                        <TableCell align="center"><strong>Latest %</strong></TableCell>
                        <TableCell align="center"><strong>Trend</strong></TableCell>
                        <TableCell><strong>Pathway</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.subjects.map(s => (
                        <TableRow key={s.code} hover>
                          <TableCell>{s.name}</TableCell>
                          <TableCell align="center"><strong>{s.meanPct}%</strong></TableCell>
                          <TableCell align="center"><GradeChip grade={s.grade} /></TableCell>
                          <TableCell align="center">{s.latestPct != null ? `${s.latestPct}%` : '—'}</TableCell>
                          <TableCell align="center"><Trend v={s.trend} /></TableCell>
                          <TableCell>
                            {s.pathway
                              ? <Chip size="small" label={s.pathway} sx={{ bgcolor: `${PATHWAY_COLORS[s.pathway] || '#607d8b'}18`, color: PATHWAY_COLORS[s.pathway] || '#607d8b', fontWeight: 600 }} />
                              : <Typography variant="caption" color="text.disabled">—</Typography>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Advisory guardrail */}
                <Alert severity="info" icon={false} sx={{ mt: 2, fontSize: 13 }}>
                  <strong>Advisory only.</strong> This lean is derived from academic performance to date and is intended to
                  support a conversation between the learner, teacher and parents — not to decide a learner's future.
                  Interests, aptitude and effort matter alongside these scores.
                </Alert>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
